import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';
import { Label } from './label';

type Tone = 'default' | 'destructive';

interface BaseProps {
  /** Opens the dialog. Leave out when the dialog is controlled with `open`. */
  trigger?: React.ReactNode;
  /**
   * Controlled mode, for actions launched from somewhere that unmounts on
   * click (a RowActions menu item): keep one dialog outside the menu and
   * open it from the item.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  tone?: Tone;
  confirmLabel: string;
  cancelLabel?: string;
  pending?: boolean;
}

type ConfirmDialogProps =
  | (BaseProps & {
      reasonRequired?: false;
      onConfirm: () => void | Promise<void>;
    })
  | (BaseProps & {
      reasonRequired: true;
      reasonLabel?: string;
      reasonPlaceholder?: string;
      onConfirm: (reason: string) => void | Promise<void>;
    });

/**
 * Single confirmation primitive for destructive and high-blast-radius actions.
 *
 * Ceremony scales with blast radius (per DESIGN.md "Confirm dialog"):
 *   - reversible single-row: tone="destructive", reasonRequired=true
 *   - multi-row / config-affecting: tone="destructive", description explains breakage
 *   - money-moving (submit / cancel payout): tone="default" or "destructive",
 *     description shows the actual dollar amount and rollback consequence
 *
 * Never use window.confirm / window.prompt in this app — those break the
 * editorial-data tone and pre-empt the keyboard story.
 */
export function ConfirmDialog(props: ConfirmDialogProps): JSX.Element {
  const {
    trigger,
    title,
    description,
    tone = 'default',
    confirmLabel,
    cancelLabel = 'Cancel',
    pending = false,
  } = props;

  const [ownOpen, setOwnOpen] = useState(false);
  const controlled = props.open !== undefined;
  const open = controlled ? props.open === true : ownOpen;
  const setOpen = (next: boolean): void => {
    if (!controlled) setOwnOpen(next);
    props.onOpenChange?.(next);
  };
  const [reason, setReason] = useState('');

  const reasonRequired = props.reasonRequired === true;
  const trimmedReason = reason.trim();
  const canConfirm = !pending && (!reasonRequired || trimmedReason.length > 0);

  async function handleConfirm(): Promise<void> {
    if (!canConfirm) return;
    if (reasonRequired) {
      await props.onConfirm(trimmedReason);
    } else {
      await props.onConfirm();
    }
    setOpen(false);
    setReason('');
  }

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (!next) setReason('');
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        className="sm:max-w-md"
        onKeyDown={(e) => {
          // Cmd/Ctrl+Enter submits the confirm action — matches the keyboard
          // story elsewhere in this app (creator-request comments, etc.).
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            void handleConfirm();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {reasonRequired && (
          <div className="grid gap-1.5">
            <Label htmlFor="confirm-reason">
              {props.reasonLabel ?? 'Reason'}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <textarea
              id="confirm-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={props.reasonPlaceholder ?? 'Visible in audit logs'}
              autoFocus
              rows={3}
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'destructive' ? 'destructive' : 'default'}
            disabled={!canConfirm}
            onClick={() => void handleConfirm()}
          >
            {pending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
